import * as React from "react";

import { cn } from "../lib/cn";
import {
  getSeekBarBackdropBlurStyle,
  SEEK_BAR_FOCUS_RING_CLASSES,
  WHITE_AUDIO_TRACK_BACKGROUND,
} from "../lib/shared-styles";

export type SeekBarProps = {
  value: number;
  min?: number;
  max: number;
  step?: number | "any";
  onSeek?: (value: number) => void;
  onSeekStart?: () => void;
  onSeekEnd?: (value: number) => void;
  disabled?: boolean;
  variant?: "full" | "mini";
  color?: "black" | "white";
  ariaLabel?: string;
  trackSize?: "default" | "thin";
  thumbSize?: "default" | "hidden";
  className?: string;
};

export const SeekBar = React.forwardRef<HTMLInputElement, SeekBarProps>(
  (
    {
      value,
      min = 0,
      max,
      step = "any",
      onSeek,
      onSeekStart,
      onSeekEnd,
      disabled = false,
      variant = "full",
      color = "black",
      ariaLabel = "Seek audio",
      trackSize = "default",
      thumbSize = "default",
      className,
    },
    ref,
  ) => {
    const lastValueRef = React.useRef(value);
    const boundedMax = Number.isFinite(max) ? max : 0;
    const boundedMin = Number.isFinite(min) ? min : 0;
    const currentValue = Number.isFinite(value) ? value : boundedMin;
    lastValueRef.current = currentValue;
    const progressPercent =
      boundedMax <= boundedMin
        ? 0
        : Math.min(
            Math.max(
              ((currentValue - boundedMin) / (boundedMax - boundedMin)) * 100,
              0,
            ),
            100,
          );

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      lastValueRef.current = e.currentTarget.valueAsNumber;
      onSeek?.(e.currentTarget.valueAsNumber);
    };

    const handleSeekStart = () => {
      if (disabled) return;
      onSeekStart?.();
    };

    const handleSeekEnd = () => {
      if (disabled) return;
      onSeekEnd?.(lastValueRef.current);
    };

    const rangeHeight =
      variant === "mini" || trackSize === "thin" ? "0.25rem" : "0.5rem";
    const colorVars =
      color === "white"
        ? {
            "--progress-bar-bg": WHITE_AUDIO_TRACK_BACKGROUND,
            "--progress-bar-fg": "hsl(var(--white))",
            "--progress-bar-thumb": "hsl(var(--white))",
          }
        : undefined;
    const thumbVars =
      thumbSize === "hidden"
        ? {
            "--progress-bar-thumb-size": "0px",
            "--progress-bar-thumb-active-scale": "1",
          }
        : {
            "--progress-bar-thumb-size": "12px",
            // Grow the 12px thumb to 16px while pressed (16 / 12).
            "--progress-bar-thumb-active-scale": "1.3333",
          };

    return (
      <input
        ref={ref}
        type="range"
        aria-label={ariaLabel}
        aria-disabled={disabled}
        disabled={disabled}
        min={String(boundedMin)}
        step={String(step)}
        max={String(boundedMax)}
        value={currentValue}
        className={cn(
          "w-full rounded-full",
          SEEK_BAR_FOCUS_RING_CLASSES,
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          className,
        )}
        style={
          {
            "--range-height": rangeHeight,
            "--range-progress": `${progressPercent}%`,
            ...getSeekBarBackdropBlurStyle(color),
            ...colorVars,
            ...thumbVars,
          } as React.CSSProperties
        }
        onInput={handleInput}
        onMouseDown={handleSeekStart}
        onTouchStart={handleSeekStart}
        onMouseUp={handleSeekEnd}
        onTouchEnd={handleSeekEnd}
        data-testid="audio-player-seek"
      />
    );
  },
);
SeekBar.displayName = "SeekBar";
